import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeGet } from '@/services/lukita'
import type { CreditBundle, CreditBundleDTO, Wallet } from './types'

interface CreditBundlesStoreActions {
  setCreditBundles: (
    wallet: Wallet,
    bundles: CreditBundle[] | CreditBundleDTO[],
  ) => void
  startLoading: (wallet: Wallet) => void
  stopLoading: (wallet: Wallet) => void
}

interface CreditBundlesStoreLoaders {
  fetchCreditBundles: (wallet: Wallet) => Promise<void>
}

interface CreditBundlesStore {
  creditBundles: Record<Wallet, CreditBundle[]>
  loadingCreditBundles: Record<Wallet, boolean>
  actions: CreditBundlesStoreActions
  loaders: CreditBundlesStoreLoaders
}

export const useCreditBundlesStore = create<CreditBundlesStore>()(
  (set, get) => ({
    creditBundles: { pages: [], screenings: [] },
    loadingCreditBundles: { pages: false, screenings: false },

    actions: {
      setCreditBundles: (wallet, bundles) =>
        set((state) => ({
          creditBundles: {
            ...state.creditBundles,
            [wallet]: camelcaseKeys(bundles),
          },
        })),
      startLoading: (wallet) =>
        set((state) => ({
          loadingCreditBundles: {
            ...state.loadingCreditBundles,
            [wallet]: true,
          },
        })),
      stopLoading: (wallet) =>
        set((state) => ({
          loadingCreditBundles: {
            ...state.loadingCreditBundles,
            [wallet]: false,
          },
        })),
    },

    loaders: {
      fetchCreditBundles: async (wallet) => {
        const { actions } = get()
        actions.startLoading(wallet)
        const response = await executeGet(`/prices/credit_bundles`, {
          queryParams: { type: wallet },
        })
        const result = await response.json()
        actions.setCreditBundles(wallet, result)
        actions.stopLoading(wallet)
      },
    },
  }),
)

export const useCreditBundles = (wallet: Wallet) =>
  useCreditBundlesStore((state) => state.creditBundles[wallet])

export const useLoadingCreditBundles = (wallet: Wallet) =>
  useCreditBundlesStore((state) => state.loadingCreditBundles[wallet])

export const useCreditBundlesLoaders = () =>
  useCreditBundlesStore((state) => state.loaders)
